import { Component, Inject, SecurityContext } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { format, parseISO } from 'date-fns';
import { Observable, combineLatest, map } from 'rxjs';

import { ApiParams, CoreApiService, queryAll } from '../../../core-api.service';
import { LoggingService } from '../../../logging.service';
import { Paginated } from '../../../shared/models/paginated.model';
import { DryrunTaskDeletionPreview } from '../../../shared/models/deprecated-task.model';
import { User } from '../../../shared/models/user.model';
import { TranslationService } from '../../../translation.service';
import { getTaskStatusDisplay } from '../../../deprecated-tasks/tasks.utils';

interface DeletePreviewRow extends Omit<DryrunTaskDeletionPreview, 'status'> {
  user: User
  status: string
}

@Component({
  host: {
    'class': 'mat-dialog-component',
    'test-id': 'delete-task-preview-dialog',
  },
  templateUrl: './delete-task-preview-dialog.component.html',
  styleUrls: ['./delete-task-preview-dialog.component.scss'],
})
export class DeleteTaskPreviewDialogComponent {

  public isLoading = true;
  public totalRows = 0;
  public tasks$: Observable<DeletePreviewRow[]>;
  public tableColumns = ['label', 'container', 'assignee', 'dueDate', 'status'];
  public warningHTML: SafeHtml = '';
  private warningString = '';
  private users: User[] = [];

  constructor(
    public dialogRef: MatDialogRef<DeleteTaskPreviewDialogComponent>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private sanitizer: DomSanitizer,
    private tranlate: TranslationService,
    @Inject(MAT_DIALOG_DATA) public data: { protocolId: string, protocolLabel: string }
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.tranlate.getMessage('viewerProtocols.protocolList.delete.tableMessage'),
      queryAll(this.coreApi.users.query),
    ]).subscribe({
      next: ([message, users]) => {
        this.users = users;
        this.warningString = message;
        this.loadPage(0);
      },
      error: (error) => {
        this.logger.error(error);
      },
    });
  }

  public confirm(): void {
    this.dialogRef.close(true);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public loadPage(index: number): void {
    const queryParams: ApiParams = {
      limit: 10,
    };
    if (index > 0) {
      queryParams.page = index + 1;
    }

    this.isLoading = true;
    this.tasks$ = this.coreApi.readTaskProtocols.deleteDryrun(this.data.protocolId, queryParams)
      .pipe(map((tasks: Paginated<DryrunTaskDeletionPreview[]>) => {
        this.totalRows = tasks.total;
        const sanitizedLabel = this.sanitizer.sanitize(SecurityContext.HTML, this.data.protocolLabel);
        this.warningHTML = this.sanitizer.bypassSecurityTrustHtml(this.tranlate.interpolate(this.warningString, sanitizedLabel, this.totalRows));
        this.isLoading = false;

        return tasks.results.map(task => ({
          ...task,
          due_date: task.due_date ? format(parseISO(task.due_date), 'MMM dd, yyyy') : '',
          status: getTaskStatusDisplay(task.status),
          user: this.users.find(user => user._id === task.assignee),
        }));
      }));
  }
}
